import { isSupportedSmartShunt, SMARTSHUNT_SUPPLY } from "../monitor-power-policy";
import { effectiveTerminals } from "../effective";
import { isSenseTerminal } from "../sense-terminal";
import { outputPortCurrent } from "../output-current-limits";
import { effectiveChargeCurrent } from "../charge-current";
import { hasActiveAcSupply } from "../ac-supply";
import { isAcTerminalId } from "../ac";
import { computeBatterySourceRanges, type BatteryStack } from "../battery-stack";
import type { SimulateContext } from "../cable-current";
import type { DefLookup } from "../catalog";
import { componentSideVoltage } from "../component-current";
import { resolveTerminalPotentials } from "../terminal-voltage";
import { getStateOption } from "../simulate/states";
import { dcPortTerminalGroups, isDirectDcSource, isPvTerminalId, isRegulatedDcSource } from "../component-ports";
import type { ComponentDefinition, ComponentNode, System, Terminal } from "../types";
import { buildGraph, chargerEngineDetection, computeActiveSolarPaths, computeActiveSourceCircuits, engineDetectionAllowsCharging, isDcSideSupplied, reachable, v } from "./graph";

/** The existing energy model's typical alternator derate and converter efficiency.
 * Output current ratings describe delivered current, so conversion loss belongs
 * on the input side and must never become a second external generation source. */
export const ALTERNATOR_DERATE = 0.85;
export const DC_CONVERSION_EFFICIENCY = 0.9;

export type EnergyBus = {
  id: string;
  nominalV: number;
  stacks: BatteryStack[];
  loadW: number;
  generationW: number;
  transferInW: number;
  transferOutW: number;
  netW: number;
};
export type EnergyFlow = {
  buses: Map<string, EnergyBus>;
  busByNode: Map<string, string>;
  busByBattery: Map<string, string>;
  harvest: { solar: number; alternator: number; shore: number; other: number };
  conversionLossW: number;
  /** Signed current injected by each active device into its physical DC
   * terminal. Battery allocation is added by the conductor-flow consumer. */
  terminalCurrents: Map<string, number>;
  /** Dedicated Vbatt+ leads stay outside the conducting graph. */
  monitorSupplyCableCurrents: Map<string, number>;
  monitorDrawW: Map<string, number>;
};

const positive = (t: Terminal) => !isAcTerminalId(t.id) && t.polarity === "positive";
const negative = (t: Terminal) => !isAcTerminalId(t.id) && (t.polarity === "negative" || t.polarity === "ground");

/** One physical DC circuit is a pair of conductor regions. A shared negative
 * alone never combines banks, and converters never short their two buses. */
export function computeEnergyFlow(
  system: System,
  lookup: DefLookup,
  simulate: SimulateContext,
  loadWatts: ReadonlyMap<string, number>,
  solarWatts: ReadonlyMap<string, number>,
): EnergyFlow {
  const graph = buildGraph(system, lookup, { simulate, excludeSense: true });
  const potentials = resolveTerminalPotentials(system, lookup, graph, simulate);
  const pairVoltages = new Map<string, number>();
  const nodesById = Object.fromEntries(system.nodes.map(node => [node.id, node]));
  const regions = new Map<string, number>();
  let nextRegion = 0;
  const region = (vertex: string): number => {
    const known = regions.get(vertex);
    if (known != null) return known;
    const id = nextRegion++;
    for (const member of reachable(graph, vertex)) regions.set(member, id);
    return id;
  };
  const pairKey = (pos: string, neg: string): string | undefined => {
    const p = region(pos), n = region(neg);
    if (p === n) return undefined;
    const id = `${p}|${n}`;
    const voltage = potentials.voltageBetween(pos, neg);
    if (voltage != null && voltage > 0) pairVoltages.set(id, voltage);
    return id;
  };
  const nodes = system.nodes.filter((n): n is ComponentNode => n.kind === "component");
  const definitions = new Map(nodes.map(n => [n.id, lookup(n.definitionId, n.customDefinition)]));
  const buses = new Map<string, EnergyBus>();
  const busByNode = new Map<string, string>();
  const busByBattery = new Map<string, string>();
  const ensureBus = (id: string, nominalV: number): EnergyBus => {
    let bus = buses.get(id);
    if (!bus) {
      bus = { id, nominalV: pairVoltages.get(id) ?? nominalV, stacks: [], loadW: 0, generationW: 0, transferInW: 0, transferOutW: 0, netW: 0 };
      buses.set(id, bus);
    }
    return bus;
  };
  const largestRangeByBattery = new Map<string, number>();
  for (const stack of computeBatterySourceRanges(system, lookup, graph, simulate)) {
    const id = stack.topPositives[0] && stack.bottomNegatives[0] && pairKey(stack.topPositives[0], stack.bottomNegatives[0]);
    if (!id) continue;
    ensureBus(id, stack.nominalV).stacks.push(stack);
    for (const batteryId of stack.batteryNodeIds) {
      if ((largestRangeByBattery.get(batteryId) ?? 0) < stack.levels.length) {
        busByBattery.set(batteryId, id);
        largestRangeByBattery.set(batteryId, stack.levels.length);
      }
    }
  }
  const sidePairs = (node: ComponentNode, def: ComponentDefinition, direction?: "input" | "output"): string[] => {
    let ports = direction ? dcPortTerminalGroups(def, direction) : [def.terminals];
    // Combined engine starter/alternator terminals use the input direction.
    if (def.category === "engine" && direction === "output" && ports.length === 0) ports = [def.terminals];
    const pairs = new Set<string>();
    for (const port of ports) for (const p of port.filter(positive)) for (const n of port.filter(negative)) {
      const id = pairKey(v(node.id, p.id), v(node.id, n.id));
      if (id) pairs.add(id);
    }
    return [...pairs];
  };
  const side = (node: ComponentNode, def: ComponentDefinition, direction?: "input" | "output"): string | undefined => {
    const pairs = sidePairs(node, def, direction);
    return pairs.find(id => buses.has(id)) ?? pairs[0];
  };
  const terminalCurrents = new Map<string, number>();
  const recordPortCurrent = (node: ComponentNode, port: readonly Terminal[], amps: number) => {
    const pos = port.filter(positive), neg = port.filter(negative);
    for (const [terms, sign] of [[pos, 1], [neg, -1]] as const) for (const term of terms) {
      const vertex = v(node.id, term.id);
      terminalCurrents.set(vertex, (terminalCurrents.get(vertex) ?? 0) + sign * amps / terms.length);
    }
  };
  const recordCurrent = (node: ComponentNode, def: ComponentDefinition, direction: "input" | "output" | undefined, id: string, amps: number) => {
    let ports = direction ? dcPortTerminalGroups(def, direction) : [def.terminals];
    if (def.category === "engine" && ports.length === 0) ports = [def.terminals];
    const matching = ports.filter(port => port.filter(positive).some(p => port.filter(negative).some(n => pairKey(v(node.id, p.id), v(node.id, n.id)) === id)));
    for (const port of matching) recordPortCurrent(node, port, amps / matching.length);
  };
  for (const node of nodes) {
    const def = definitions.get(node.id);
    // An inactive inverter draw is not a consuming circuit. Creating a bus for
    // its zero-watt input would let charging feed a floating DC output pair.
    if (!def || !((loadWatts.get(node.id) ?? 0) > 0)) continue;
    const id = side(node, def, "input") ?? side(node, def);
    if (!id) continue;
    busByNode.set(node.id, id);
    const bus = ensureBus(id, def.voltage ?? def.compatibleVoltages?.[0] ?? 12);
    const watts = loadWatts.get(node.id) ?? 0;
    bus.loadW += watts;
    recordCurrent(node, def, "input", id, -watts / bus.nominalV);
  }

  const monitorSupplyCableCurrents = new Map<string, number>();
  const monitorDrawW = new Map<string, number>();
  const circuits = computeActiveSourceCircuits(system, lookup, simulate);
  for (const node of nodes) {
    const def = definitions.get(node.id);
    if (!def || !isSupportedSmartShunt(def, node) || def.ampDraw != null) continue;
    const leads = system.cables.flatMap(cable => {
      const fromMonitor = cable.fromNodeId === node.id && cable.fromTerminalId === "vbat";
      if (!fromMonitor && !(cable.toNodeId === node.id && cable.toTerminalId === "vbat")) return [];
      monitorSupplyCableCurrents.set(cable.id, 0);
      const peerId = fromMonitor ? cable.toNodeId : cable.fromNodeId;
      const peerTermId = fromMonitor ? cable.toTerminalId : cable.fromTerminalId;
      const peer = nodesById[peerId], peerDef = definitions.get(peerId);
      if (peer?.kind !== "component" || !peerDef || isAcTerminalId(peerTermId)) return [];
      const term = effectiveTerminals(peerDef, peer).find(t => t.id === peerTermId);
      if (!term || isSenseTerminal(peerDef, term)) return [];
      return [{ cable, fromMonitor, positive: v(peerId, peerTermId) }];
    });
    // Vbatt+ terminates its own electronics supply. Never join two buses
    // through this input, or count an orphaned cable as a second supply.
    if (leads.length !== 1) continue;
    const [{ cable, fromMonitor, positive }] = leads;
    const negative = v(node.id, "batt-neg");
    const energized = simulate.energizedTerminals;
    if (energized && !(energized.has(positive) && energized.has(negative))) continue;
    if (!circuits.some(source => source.positive.has(positive) && source.negative.has(negative))) continue;
    const volts = potentials.voltageBetween(positive, negative);
    if (volts == null || volts < SMARTSHUNT_SUPPLY.minimumV || volts > SMARTSHUNT_SUPPLY.maximumV) continue;
    const id = pairKey(positive, negative);
    if (!id) continue;
    const amps = SMARTSHUNT_SUPPLY.drawUpperBoundA;
    const bus = ensureBus(id, volts);
    const watts = amps * bus.nominalV;
    busByNode.set(node.id, id);
    monitorDrawW.set(node.id, watts);
    monitorSupplyCableCurrents.set(cable.id, fromMonitor ? -amps : amps);
    bus.loadW += watts;
    terminalCurrents.set(v(node.id, "vbat"), -amps);
    terminalCurrents.set(negative, (terminalCurrents.get(negative) ?? 0) + amps);
  }

  const harvest = { solar: 0, alternator: 0, shore: 0, other: 0 };
  const addGeneration = (id: string | undefined, watts: number, kind: keyof typeof harvest) => {
    const bus = id && buses.get(id);
    if (!bus || watts <= 0) return;
    bus.generationW += watts;
    harvest[kind] += watts;
  };
  const outputAmps = (node: ComponentNode, def: ComponentDefinition, rated = false): number =>
    Math.max(0, effectiveChargeCurrent(node, def, simulate,
      rated ? def.ampOutput ?? def.chargeAmpsTypical : def.chargeAmpsTypical ?? def.ampOutput) ?? 0);
  const running = (node: ComponentNode, def: ComponentDefinition) => outputAmps(node, def) > 0;

  type Converter = { id: string; input: string; output: string; outputV: number; capacityW: number; outputW: number; efficiency: number };
  const converters: Converter[] = [];
  type AcOutput = { id: string; terminals: readonly Terminal[]; limitA: number; currentA: number };
  const acChargers: { node: ComponentNode; def: ComponentDefinition; outputs: AcOutput[] }[] = [];
  for (const node of nodes) {
    const def = definitions.get(node.id);
    if (!def) continue;
    const output = side(node, def, "output") ?? side(node, def);
    if (isDirectDcSource(def)) {
      if (!running(node, def)) continue;
      const bus = output && buses.get(output);
      if (bus) {
        if (isRegulatedDcSource(def)) {
          const option = getStateOption(def, simulate.componentStates[node.id]);
          const powerLimitW = (def.watts ?? 0) * (option?.ampMultiplier ?? 1);
          // EFOY's 3.8 A is specified at 10.5 V. At a 12 V bank, multiplying
          // that current by voltage must never exceed its 40 W power ceiling.
          const deliveredW = Math.min(outputAmps(node, def) * bus.nominalV, powerLimitW);
          addGeneration(output, deliveredW, "other");
          recordCurrent(node, def, "output", bus.id, deliveredW / bus.nominalV);
        } else {
          const amps = outputAmps(node, def) * ALTERNATOR_DERATE;
          addGeneration(output, amps * bus.nominalV, "alternator");
          recordCurrent(node, def, "output", bus.id, amps);
        }
      }
      continue;
    }
    if (def.category !== "charger" || !running(node, def)) continue;
    const dcInputs = def.terminals.some(t => positive(t) && t.direction === "input" && !isPvTerminalId(t.id));
    const acInputs = def.terminals.some(t => isAcTerminalId(t.id) && t.direction === "input");
    if (acInputs) {
      const outputs: AcOutput[] = [];
      for (const port of dcPortTerminalGroups(def, "output")) {
        const ids = new Set<string>();
        for (const p of port.filter(positive)) for (const n of port.filter(negative)) {
          const id = pairKey(v(node.id, p.id), v(node.id, n.id));
          if (id && buses.has(id)) ids.add(id);
        }
        const positiveId = port.find(positive)!.id;
        const limitA = outputPortCurrent(def, positiveId, outputAmps(node, def, true));
        for (const id of ids) outputs.push({ id, terminals: port, limitA, currentA: 0 });
      }
      if (outputs.length > 0 && hasActiveAcSupply(node, def, system, lookup, simulate)) {
        acChargers.push({ node, def, outputs });
      }
    }
    if (dcInputs) {
      if (!engineDetectionAllowsCharging(chargerEngineDetection(node, def, system, lookup, simulate, graph))) continue;
      const input = side(node, def, "input");
      if (!input || !output || input === output) continue;
      const outputV = pairVoltages.get(output) ?? buses.get(output)?.nominalV ?? componentSideVoltage(node.id, def, "output", nodesById, system.cables, lookup, def.outputVoltage ?? 12);
      const inputV = pairVoltages.get(input) ?? buses.get(input)?.nominalV ?? componentSideVoltage(node.id, def, "input", nodesById, system.cables, lookup, def.inputVoltage ?? 12);
      const declaredEfficiency = def.conversionEfficiency;
      const efficiency = declaredEfficiency != null && declaredEfficiency > 0 && declaredEfficiency <= 1 ? declaredEfficiency : DC_CONVERSION_EFFICIENCY;
      const outputPorts = dcPortTerminalGroups(def, "output").filter(port =>
        port.filter(positive).some(p => port.filter(negative).some(n => pairKey(v(node.id, p.id), v(node.id, n.id)) === output)));
      const outputA = Math.min(outputAmps(node, def), outputPorts.reduce((sum, port) =>
        sum + outputPortCurrent(def, port.find(positive)!.id, outputAmps(node, def, true)), 0));
      // Capacity is delivered power, constrained by either the output stage or
      // the declared input current at this live input pair's actual voltage.
      const inputLimitA = def.maxInputA != null && def.maxInputA > 0 ? def.maxInputA : Infinity;
      converters.push({ id: node.id, input, output, outputV, capacityW: Math.min(outputA * outputV, inputLimitA * inputV * efficiency),
        outputW: 0, efficiency });
      ensureBus(input, inputV);
      ensureBus(output, outputV);
    }
  }

  // Keep panel identity tied to complete directed paths on each controller.
  // A shared return alone must never redirect generation to another bank.
  const paths = computeActiveSolarPaths(system, lookup, simulate);
  const requestedSolar = new Map<string, number>();
  for (const [nodeId, watts] of solarWatts) {
    if (watts <= 0) continue;
    const node = nodes.find(n => n.id === nodeId), def = definitions.get(nodeId);
    if (!node || !def) continue;
    const direct = side(node, def);
    if (direct && buses.get(direct)?.stacks.length) {
      addGeneration(direct, watts, "solar");
      recordCurrent(node, def, undefined, direct, watts / buses.get(direct)!.nominalV);
      continue;
    }
    const controllers = paths.filter(path => path.panelNodeIds.has(nodeId));
    // A shared PV array remains one source. Without an MPPT interaction model,
    // allocate its available power equally across its connected controllers.
    for (const { controllerNodeId } of controllers) requestedSolar.set(controllerNodeId,
      (requestedSolar.get(controllerNodeId) ?? 0) + watts / controllers.length);
  }
  const controllerSolarW = new Map<string, number>();
  const hybridLimitA = (node: ComponentNode, def: ComponentDefinition): number => {
    const limitA = def.hybridSourceOutputLimitA;
    return limitA != null && Number.isFinite(limitA) && limitA > 0 &&
      isDcSideSupplied(node, def, "input", circuits)
      ? limitA : Infinity;
  };
  for (const [controllerId, watts] of requestedSolar) {
    const node = nodes.find(n => n.id === controllerId)!, def = definitions.get(controllerId)!;
    const outputs: Array<{ id: string; voltage: number; terminals: Set<string>; physicalTerminals: readonly Terminal[]; currentA: number }> = [];
    const terminalRemainingA = new Map<string, number>();
    for (const port of dcPortTerminalGroups(def, "output")) {
      const ids = new Set<string>();
      for (const p of port.filter(positive)) for (const n of port.filter(negative)) {
        const id = pairKey(v(node.id, p.id), v(node.id, n.id));
        // Match both rails to one actual bank. A different output's return,
        // a shared negative alone or a floating load pair cannot qualify it.
        if (id && buses.get(id)?.stacks.length) ids.add(id);
      }
      if (ids.size === 0) continue;
      const terminals = new Set(port.map(t => t.id));
      for (const terminal of port.filter(positive)) {
        terminalRemainingA.set(terminal.id, outputPortCurrent(def, terminal.id, outputAmps(node, def, true)));
      }
      for (const id of ids) outputs.push({ id, voltage: buses.get(id)!.nominalV, terminals, physicalTerminals: port, currentA: 0 });
    }
    // Neutral equal-current allocation across complete output ports, not a
    // prediction of a controller's configured bank priority or charge stage.
    // Water-fill the remaining ports when one reaches its own limit. Track
    // positive outputs once; connector ampacities do not limit generation.
    let remainingA = Math.min(outputAmps(node, def, true), hybridLimitA(node, def)), remainingW = watts;
    for (let pass = 0; pass <= outputs.length + terminalRemainingA.size && remainingA > 1e-9 && remainingW > 1e-9; pass++) {
      const available = outputs.filter(port => [...port.terminals].every(id => (terminalRemainingA.get(id) ?? Infinity) > 1e-9));
      if (available.length === 0) break;
      const usersByTerminal = new Map<string, number>();
      for (const port of available) for (const id of port.terminals) usersByTerminal.set(id, (usersByTerminal.get(id) ?? 0) + 1);
      const volts = available.reduce((sum, port) => sum + port.voltage, 0);
      const share = Math.min(remainingA / available.length, remainingW / volts,
        ...[...usersByTerminal].map(([id, users]) => (terminalRemainingA.get(id) ?? Infinity) / users));
      if (share <= 1e-9) break;
      for (const port of available) port.currentA += share;
      for (const [id, users] of usersByTerminal) if (terminalRemainingA.has(id)) {
        terminalRemainingA.set(id, Math.max(0, terminalRemainingA.get(id)! - share * users));
      }
      remainingA = Math.max(0, remainingA - share * available.length);
      remainingW = Math.max(0, remainingW - share * volts);
    }
    let delivered = 0;
    for (const port of outputs) {
      const power = port.currentA * port.voltage;
      addGeneration(port.id, power, "solar");
      recordPortCurrent(node, port.physicalTerminals, port.currentA);
      delivered += power;
    }
    controllerSolarW.set(controllerId, delivered);
  }
  // Multi-input chargers share ONE output stage. Solar is allocated first;
  // AC then vehicle DC is a planning convention, not a prediction of firmware
  // source sharing. Keep ordinary AC-only chargers' equal-bank allocation.
  const controllerAcW = new Map<string, number>();
  for (const { node, def, outputs } of acChargers) {
    // Preserve the planning model's equal bank allocation, then retain
    // each physical port's share and limit on that bank. Joining two ports
    // to one bank must not give it twice another bank's charging budget.
    const shareCurrent = (targets: { limitA: number; currentA: number }[], requestedA: number) => {
      let remainingA = requestedA;
      for (let pass = 0; pass <= targets.length && remainingA > 1e-9; pass++) {
        const available = targets.filter(port => port.currentA < port.limitA);
        if (available.length === 0) break;
        const share = remainingA / available.length;
        for (const port of available) {
          const added = Math.min(share, port.limitA - port.currentA);
          port.currentA += added;
          remainingA -= added;
        }
      }
    };
    const banks = new Map<string, { limitA: number; currentA: number }>();
    for (const port of outputs) banks.set(port.id, { limitA: (banks.get(port.id)?.limitA ?? 0) + port.limitA, currentA: 0 });
    const solarW = controllerSolarW.get(node.id) ?? 0;
    const outputV = buses.get(outputs[0].id)!.nominalV;
    const remainingA = Math.max(0, outputAmps(node, def, true) - solarW / outputV);
    shareCurrent([...banks.values()], Math.min(outputAmps(node, def), remainingA));
    for (const [id, bank] of banks) shareCurrent(outputs.filter(port => port.id === id), bank.currentA);
    for (const port of outputs) {
      const watts = port.currentA * buses.get(port.id)!.nominalV;
      controllerAcW.set(node.id, (controllerAcW.get(node.id) ?? 0) + watts);
      addGeneration(port.id, watts, "shore");
      recordPortCurrent(node, port.terminals, port.currentA);
    }
  }
  for (const converter of converters) {
    const node = nodes.find(n => n.id === converter.id)!, def = definitions.get(converter.id)!;
    const limitW = outputAmps(node, def, true) * converter.outputV;
    const solarW = controllerSolarW.get(converter.id) ?? 0;
    converter.capacityW = Math.min(converter.capacityW, Math.max(0, limitW - solarW - (controllerAcW.get(converter.id) ?? 0)),
      solarW > 0 ? hybridLimitA(node, def) * converter.outputV : Infinity);
  }

  // Propagate usable input supply forward, never from a charged output bank
  // backwards through an isolated charger. A converter with a floating input
  // cannot transfer power, even if its output is connected to a full battery.
  const live = new Set([...buses].filter(([, bus]) => bus.stacks.length > 0 || bus.generationW > 0).map(([id]) => id));
  for (let pass = 0; pass < converters.length; pass++) for (const converter of converters) if (live.has(converter.input)) live.add(converter.output);
  // Downstream battery chargers request their configured output; a batteryless
  // output requests only its connected loads and downstream conversion demand.
  for (let pass = 0; pass <= converters.length; pass++) {
    for (const converter of converters) {
      const out = buses.get(converter.output)!;
      const downstreamW = converters.filter(c => c.input === converter.output).reduce((s, c) => s + c.outputW / c.efficiency, 0);
      const sharedCapacityW = converters.filter(c => c.output === converter.output && live.has(c.input)).reduce((s, c) => s + c.capacityW, 0);
      const requestW = out.stacks.length > 0 ? converter.capacityW : (out.loadW + downstreamW) * converter.capacityW / (sharedCapacityW || 1);
      converter.outputW = live.has(converter.input) ? Math.min(converter.capacityW, requestW) : 0;
    }
  }
  // A batteryless intermediate bus cannot supply more converter input power
  // than actually arrives. Reductions propagate through converter chains.
  for (let pass = 0; pass <= converters.length; pass++) for (const bus of buses.values()) {
    if (bus.stacks.length > 0) continue;
    const outgoing = converters.filter(c => c.input === bus.id);
    const requestedW = outgoing.reduce((s, c) => s + c.outputW / c.efficiency, 0);
    const incomingW = converters.filter(c => c.output === bus.id).reduce((s, c) => s + c.outputW, 0);
    const availableW = Math.max(0, bus.generationW + incomingW - bus.loadW);
    if (requestedW > availableW) for (const converter of outgoing) converter.outputW *= availableW / requestedW;
  }
  let conversionLossW = 0;
  for (const converter of converters) {
    const inputW = converter.outputW / converter.efficiency;
    buses.get(converter.input)!.transferOutW += inputW;
    buses.get(converter.output)!.transferInW += converter.outputW;
    conversionLossW += inputW - converter.outputW;
    const node = nodes.find(n => n.id === converter.id)!, def = definitions.get(converter.id)!;
    recordCurrent(node, def, "input", converter.input, -inputW / buses.get(converter.input)!.nominalV);
    recordCurrent(node, def, "output", converter.output, converter.outputW / converter.outputV);
  }
  for (const bus of buses.values()) bus.netW = bus.generationW + bus.transferInW - bus.loadW - bus.transferOutW;
  return { buses, busByNode, busByBattery, harvest, conversionLossW, terminalCurrents, monitorSupplyCableCurrents, monitorDrawW };
}
